<template>
  <v-container fluid class="pa-0 bg-slate-50 min-h-screen" style="font-family: 'Inter', sans-serif;">
    <div class="p-4 md:p-8">
      <div class="max-w-5xl mx-auto">
        
        <!-- HEADER -->
        <div class="mb-8 flex items-center justify-between">
          <div>
            <button 
              @click="$router.push(`/biddings-manager/edit/${data.bidding_id}`)"
              class="flex items-center gap-2 text-slate-500 hover:text-orange-600 transition-all mb-2 group"
            >
              <v-icon small color="currentColor" class="transition-transform group-hover:-translate-x-1">mdi-arrow-left</v-icon>
              <span class="text-[10px] font-black uppercase tracking-[0.2em]">Voltar</span>
            </button>
            <h1 class="text-2xl font-bold text-slate-700 tracking-tight">Contrato da Licitação</h1>
            <p class="text-sm text-slate-500 font-medium">{{ id ? 'Editando contrato' : 'Vinculando novo contrato ao participante' }}</p>
          </div>
        </div>

        <v-form ref="form" v-model="valid" lazy-validation>
          <div class="space-y-6">
            
            <!-- SEÇÃO: IDENTIFICAÇÃO DO CONTRATO -->
            <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
                <v-icon color="orange" small>mdi-card-account-details-outline</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">DADOS DO CONTRATO</h2>
              </div>
              
              <div class="p-6">
                <div class="grid grid-cols-1 md:grid-cols-12 gap-6">
                  <!-- Número Licitação (Disabled) -->
                  <div class="md:col-span-3">
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Nº Licitação</label>
                    <v-text-field
                      v-model="data.bidding_number"
                      disabled
                      solo flat dense hide-details
                      class="custom-select opacity-75"
                    />
                  </div>
                  <!-- Número Contrato -->
                  <div class="md:col-span-3">
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Nº Contrato</label>
                    <v-text-field
                      v-model="data.contract_number"
                      placeholder="000/0000"
                      solo flat dense hide-details
                      class="custom-select"
                    />
                  </div>
                  <!-- Valor -->
                  <div class="md:col-span-6">
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Valor Total do Contrato</label>
                    <v-text-field
                      v-model="data.contract_value"
                      v-mask-decimal.br="2"
                      placeholder="R$ 0,00"
                      prepend-inner-icon="mdi-currency-usd"
                      solo flat dense hide-details
                      class="custom-select font-bold"
                    />
                  </div>

                  <!-- Participante (FULL WIDTH) -->
                  <div class="md:col-span-12">
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Participante / Fornecedor</label>
                    <v-autocomplete
                      v-model="data.provider_id"
                      :items="providers"
                      item-value="provider_id"
                      item-text="provider_name"
                      :rules="fieldRules"
                      placeholder="SELECIONE O PARTICIPANTE..."
                      solo flat dense hide-details
                      class="custom-select"
                      @change="loadItems(data.bidding_id, data.provider_id)"
                    />
                  </div>
                </div>
              </div>
            </section>

            <!-- SEÇÃO: VIGÊNCIA E DATAS -->
            <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
                <v-icon color="orange" small>mdi-calendar-range</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">VIGÊNCIA E DATAS</h2>
              </div>
              
              <div class="p-6">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Início da Vigência</label>
                    <v-text-field
                      v-model="data.contract_start_term"
                      v-mask-date.br
                      placeholder="DD/MM/AAAA"
                      solo flat dense hide-details
                      class="custom-select"
                    />
                  </div>
                  <div>
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Término da Vigência</label>
                    <v-text-field
                      v-model="data.contract_end_term"
                      v-mask-date.br
                      placeholder="DD/MM/AAAA"
                      solo flat dense hide-details
                      class="custom-select"
                    />
                  </div>
                  <div>
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Data de Publicação</label>
                    <v-text-field
                      v-model="data.contract_publication"
                      v-mask-date.br
                      placeholder="DD/MM/AAAA"
                      solo flat dense hide-details
                      class="custom-select"
                    />
                  </div>
                  <div>
                    <label class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Data de Assinatura</label>
                    <v-text-field
                      v-model="data.contract_signature"
                      v-mask-date.br
                      placeholder="DD/MM/AAAA"
                      solo flat dense hide-details
                      class="custom-select"
                    />
                  </div>
                </div>
              </div>
            </section>

            <!-- SEÇÃO: OBJETO -->
            <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
                <v-icon color="orange" small>mdi-text-box-outline</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">OBJETO DO CONTRATO</h2>
              </div>
              <div class="p-6">
                <v-textarea
                  v-model="data.contract_obj"
                  placeholder="DESREVA O OBJETO DO CONTRATO..."
                  solo flat dense hide-details
                  rows="4"
                  class="custom-field bg-slate-50 rounded-lg p-2 border border-slate-200"
                />
              </div>
            </section>

            <!-- SEÇÃO: ITENS DO PARTICIPANTE -->
            <section v-if="items.length > 0" class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
                <v-icon color="orange" small>mdi-format-list-bulleted</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">ITENS DO PARTICIPANTE NESTA LICITAÇÃO</h2>
              </div>
              <div class="p-0">
                <v-data-table 
                  :headers="headers" 
                  :items="items"
                  hide-default-footer
                  class="custom-table"
                >
                  <template v-slot:item.item_price="{ item }">
                    <span class="font-bold">R$ {{ realMask(item.item_price.toFixed(2)) }}</span>
                  </template>
                  <template v-slot:item.item_billing_price="{ item }">
                    <span>R$ {{ realMask(item.item_billing_price.toFixed(2)) }}</span>
                  </template>
                </v-data-table>
              </div>
            </section>

            <!-- BOTÕES DE AÇÃO -->
            <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div class="bg-slate-50 px-8 py-5 flex justify-end items-center gap-4">
                <button 
                  @click="$router.push(`/biddings-manager/edit/${data.bidding_id}`)"
                  class="text-slate-500 hover:text-slate-700 font-bold text-[11px] uppercase px-4 py-2 transition-colors flex-none"
                >
                  Cancelar
                </button>
                <div class="flex-none">
                  <button
                    @click="validate"
                    :disabled="!valid || loader"
                    class="bg-orange-500 hover:bg-orange-600 text-white font-bold text-[11px] px-6 py-2.5 rounded shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all w-auto max-w-max whitespace-nowrap flex items-center gap-2"
                  >
                    <v-icon small color="white" class="mr-1" style="font-size: 14px;">mdi-check</v-icon>
                    <span v-if="loader" class="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span>
                    <span v-else>{{ id ? 'SALVAR ALTERAÇÕES' : 'CONFIRMAR CADASTRO' }}</span>
                  </button>
                </div>
              </div>
            </section>

          </div>
        </v-form>
      </div>
    </div>

    <!-- SNACKBAR -->
    <v-snackbar multi-line border="left" :color="snack.color" v-model="snack.show" transition="scroll-y-reverse-transition" class="mb-6">
      <div class="flex items-center gap-3">
        <v-icon color="white">mdi-information-outline</v-icon>
        <span class="font-bold text-xs uppercase tracking-wider text-white">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
export default {
  props: ["b_id", "id"],
  data: () => ({
    valid: false,
    loader: false,
    biddings: [],
    providers: [],
    biddings_types: [],
    biddings_modalitites: [],
    items: [],
    headers: [
      {
        text: "Número",
        align: "start",
        sortable: false,
        value: "item_number",
      },
      { text: "Descrição", value: "item_description_desc" },
      { text: "Prc Unitário", value: "item_price" },
      { text: "Prc Lic Total", value: "item_billing_price" },
      { text: "Quantidade", value: "item_amount" },
      { text: "Quant. Disp", value: "item_available" },
      { text: "Status", value: "item_status" },
      { text: "Status Retroactive", value: "item_status_retroactive" },
    ],
    fieldRules: [(v) => !!v || "Este campo é requerido"],
    snack: {
      show: false,
      message: "",
      color: "success",
    },
    data: {
      bidding_id: null,
      bidding_number: null,
      contract_number: null,
      modality_id: null,
      provider_id: null,
      type_id: null,
      contract_value: 0,
      contract_start_term: "",
      contract_end_term: "",
      contract_publication: "",
      contract_signature: "",
      contract_obj: "",
    },
  }),
  created() {
    this.$http.get(`/biddings/${this.b_id}`).then((r) => {
      this.biddings = r.data;
      this.data.bidding_number = r.data[0].bidding_process_number;
    });
    this.$http.get(`/providers`).then((r) => (this.providers = r.data));

    this.$http
      .get(`/biddings/types`)
      .then((r) => (this.biddings_types = r.data));
    this.$http
      .get(`/biddings/modalities`)
      .then((r) => (this.biddings_modalitites = r.data));
    if (this.id) {
      this.$store.dispatch("setLoader", true);
      this.$http.get(`/biddings/contracts/single/${this.id}`).then((r) => {
        this.data = r.data[0];
        if (this.data.contract_start_term) {
          this.data.contract_start_term = this.formatDateBr(
            this.data.contract_start_term
          );
        }
        if (this.data.contract_end_term) {
          this.data.contract_end_term = this.formatDateBr(
            this.data.contract_end_term
          );
        }
        if (this.data.contract_publication) {
          this.data.contract_publication = this.formatDateBr(
            this.data.contract_publication
          );
        }
        if (this.data.contract_signature) {
          this.data.contract_signature = this.formatDateBr(
            this.data.contract_signature
          );
        }

        this.data.contract_value = this.data.contract_value
          ? this.data.contract_value.toFixed(2)
          : 0;
        this.loadItems(this.data.bidding_id, this.data.provider_id);
        this.$store.dispatch("setLoader", false);
      });
    } else {
      this.data.bidding_id = Number(this.b_id);
    }
  },
  methods: {
    realMask(value) {
      value = value.replace(/\D/g, ""); // permite digitar apenas numero
      value = value.replace(/(\d{1})(\d{14})$/, "$1.$2"); // coloca ponto antes dos ultimos digitos
      value = value.replace(/(\d{1})(\d{11})$/, "$1.$2"); // coloca ponto antes dos ultimos 11 digitos
      value = value.replace(/(\d{1})(\d{8})$/, "$1.$2"); // coloca ponto antes dos ultimos 8 digitos
      value = value.replace(/(\d{1})(\d{5})$/, "$1.$2"); // coloca ponto antes dos ultimos 5 digitos
      value = value.replace(/(\d{1})(\d{1,2})$/, "$1,$2"); // coloca virgula antes dos ultimos 2 digitos
      return value;
    },
    formatDateBr(date) {
      return date.substr(0, 10).split("-").reverse().join("/");
    },
    loadItems(bidding_id, provider_id) {
      this.$http
        .get(`/biddings/contracts/items/${bidding_id}/${provider_id}`)
        .then((r) => (this.items = r.data));
    },
    validate() {
      this.snack.show = true;
      this.loader = true;
      if (this.$refs.form.validate()) {
        this.createOrUpdate();
      } else {
        this.snack.show = true;
        this.snack.color = "warning";
        this.snack.message =
          "Atencão um ou mais campos precisam ser preenchidos!";
        this.loader = false;
      }
    },
    createOrUpdate() {
      this.loader = true;
      if (this.id) {
        this.$store.dispatch("setLoader", this.loader);
        this.$http
          .put(`/biddings/contracts/${this.id}`, this.data)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.loader = true;
            setTimeout(() => {
              this.$router.push(
                `/biddings-manager/edit/${this.data.bidding_id}`
              );
            }, 1500);
            this.$store.dispatch("setLoader", this.loader);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      } else {
        this.$store.dispatch("setLoader", this.loader);
        this.$http
          .post("/biddings/contracts", this.data)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.loader = false;
            this.$store.dispatch("setLoader", this.loader);
            setTimeout(() => {
              this.$router.push(
                `/biddings-manager/edit/${this.data.bidding_id}`
              );
            }, 1500);
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.loader = false;
            this.$store.dispatch("setLoader", false);
          });
      }
    },
  },
};
</script>

<style scoped>
.title {
  text-transform: uppercase;
  font-weight: 400;
}

.flexd {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
}
.flexd p {
  margin: 10px 0;
  font-weight: bold;
  text-transform: uppercase;
}
.custom-select ::v-deep .v-input__slot {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  border-radius: 4px !important;
  height: 48px !important;
  padding: 0 16px !important;
}

.custom-select ::v-deep input {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #334155 !important;
  text-transform: uppercase !important;
}

.custom-select ::v-deep .v-label {
  font-size: 10px !important;
  font-weight: 900 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.15em !important;
  color: #94a3b8 !important;
}

.custom-table ::v-deep table thead th {
  background-color: #f8fafc !important;
  color: #64748b !important;
  font-size: 10px !important;
  font-weight: 800 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.1em !important;
  height: 48px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.custom-table ::v-deep table tbody td {
  height: 50px !important;
  color: #334155 !important;
  border-bottom: 1px solid #f1f5f9 !important;
  font-size: 11px !important;
}

@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.animate-spin { animation: spin 1s linear infinite; }
</style>
```